<template>
  <button :class="switchClass" :disabled="disabled" @click="toggleSwitch">
    <span :class="thumbClass"></span>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  modelValue: boolean
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false
})

interface Emits {
  (e: 'update:modelValue', value: boolean): void
}

const emit = defineEmits<Emits>()

const switchClass = computed(() => {
  const baseClasses = [
    'relative',
    'inline-flex',
    'items-center',
    'justify-center',
    'flex-shrink-0',
    'cursor-pointer',
    'transition-colors',
    'duration-200',
    'ease-in-out',
    'border-none',
    'rounded-full',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-blue-200'
  ]

  const activeClasses = props.modelValue
    ? ['bg-blue-500', 'shadow-blue-500/50']
    : ['bg-gray-300', 'shadow-gray-300/50']

  const disabledClasses = props.disabled
    ? ['opacity-50', 'cursor-not-allowed']
    : []

  return [...baseClasses, ...activeClasses, ...disabledClasses].join(' ')
})

const thumbClass = computed(() => {
  const baseClasses = [
    'rounded-full',
    'bg-white',
    'shadow-md',
    'transition-all',
    'duration-200',
    'ease-in-out'
  ]

  const positionClasses = props.modelValue ? ['left-[24px]'] : ['left-0']

  return [...baseClasses, ...positionClasses].join(' ')
})

const toggleSwitch = () => {
  if (!props.disabled) {
    emit('update:modelValue', !props.modelValue)
  }
}
</script>

<style scoped>
button {
  width: 44px;
  height: 24px;
  padding: 0;
  position: relative;
}

button span {
  width: 20px;
  height: 20px;
  position: absolute;
  top: 2px;
}
</style>
